import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage } from "@/components/layout/Page";
import { getLocalizedMeta } from "@/lib/utils/seo";
import { useI18n } from "@/context/I18nContext";
import { getCurrentLocale } from "@/lib/utils/locale";
import { COMPANY } from "@/lib/company";

export const Route = createFileRoute("/terms")({
  // Was a hand-rolled tr/en conditional with no og:/twitter: tags, so the
  // other seven locales read English and every social card fell through to
  // the homepage's. SEO_COPY carries all nine locales for this page now.
  head: () => ({ meta: getLocalizedMeta("terms") }),
  component: TermsPage,
});

function TermsPage() {
  const { lang } = useI18n();
  return lang === "tr" ? <TermsTr /> : <TermsEn />;
}

function TermsEn() {
  return (
    <LegalPage title="Terms of Service (ToS)">
      <p className="text-xs text-muted-foreground font-semibold">Effective Date: 01/08/2025</p>

      <h2>1. Introduction</h2>
      <p>
        These Terms of Service (“Terms”) govern your access to and use of the website and commercial
        services provided by <strong>{COMPANY.legalName}</strong> (“VARS”, “we”, “us”).
      </p>
      <p>
        By accessing{" "}
        <a
          href="https://varsco.com"
          className="text-primary font-bold hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          varsco.com
        </a>{" "}
        or submitting purchase orders, you agree to comply with:
      </p>
      <ul>
        <li>These Terms of Service</li>
        <li>
          Our Privacy Policy:{" "}
          <Link to="/privacy" className="text-primary font-bold hover:underline">
            /privacy
          </Link>
        </li>
        <li>
          Our KVKK Disclosure Text:{" "}
          <Link to="/kvkk-disclosure-text" className="text-primary font-bold hover:underline">
            /kvkk-disclosure-text
          </Link>
        </li>
        <li>
          Our Distance Sales Agreement (where applicable):{" "}
          <Link to="/distance-sales-agreement" className="text-primary font-bold hover:underline">
            /distance-sales-agreement
          </Link>
        </li>
      </ul>

      <h2>2. Company Information</h2>
      <p>
        <strong>Company Name:</strong> {COMPANY.legalName}
      </p>
      <p>
        <strong>Address:</strong> {COMPANY.address.full}
      </p>
      <p>
        <strong>Tax Office & Tax ID:</strong> Konak V.D. 9240533729 | <strong>MERSIS No:</strong>{" "}
        0924053372900001
      </p>
      <p>
        <strong>Email:</strong>{" "}
        <a href="mailto:info@varsco.com" className="text-primary font-bold">
          info@varsco.com
        </a>{" "}
        | <strong>Phone:</strong> +90 232 290 57 56
      </p>

      <h2>3. Scope of Services</h2>
      <p>VARS provides institutional-grade B2B aquaculture inputs and services, including:</p>
      <ul>
        <li>
          High-performance live feed (Artemia cysts, Decapsulated Artemia, Chlorella, rotifer
          enrichments)
        </li>
        <li>Certified salmonid ova & fertilized Atlantic / Coho / Rainbow Trout eggs</li>
        <li>Feed additives, raw ingredients (wheat gluten, fish meal/oil substitutes)</li>
        <li>Commercial seafood export (Aegean Sea Bass, Sea Bream, Trout, Tuna feed)</li>
        <li>Hatchery engineering design and Recirculating Aquaculture System (RAS) consulting</li>
      </ul>

      <h2>4. Relationship with Other Agreements</h2>
      <p>
        Specific commercial transactions may be governed by individual proforma invoices, sales
        contracts, or Distance Sales Agreements. In case of conflict, signed specific commercial
        contracts take precedence.
      </p>

      <h2>5. User Obligations</h2>
      <p>
        Users and purchasing agents agree to provide accurate corporate registration details,
        maintain credentials securely, and utilize products solely for legitimate aquaculture
        operations.
      </p>

      <h2>6. Orders & Commercial Terms</h2>
      <p>
        All quotation requests, proforma invoices, and purchase orders are confirmed upon written
        acceptance and validation by VARS export management.
      </p>

      <h2>7. Payment Terms</h2>
      <p>
        Online payments are securely processed via{" "}
        <strong>iyzico PCI-DSS Level 1 infrastructure</strong> accepting major credit cards (Visa,
        Mastercard, Troy, AMEX). Commercial bank transfers (SWIFT / EFT / Wire) must reference the
        official proforma invoice number.
      </p>

      <h2>8. Shipping & Risk Transfer</h2>
      <p>
        Deliveries are executed under international Incoterms (FOB, CIF, EXW, CIP). Risk transfers
        according to the specified Incoterm. Temperature data loggers verify cold-chain integrity
        during air freight and reefer cargo transport.
      </p>

      <h2>9. Product Use & Technical Responsibility</h2>
      <p>
        Live feed and biological inputs require compliance with VARS biological protocols. VARS
        provides technical guidelines but ultimate farm management rests with the buyer.
      </p>

      <h2>10. Intellectual Property</h2>
      <p>
        All trademarks, logos, REVIVE™ brand assets, and proprietary technical guides are the sole
        property of {COMPANY.legalName}.
      </p>

      <h2>11. Personal Data Protection (KVKK)</h2>
      <p>
        Personal data collected during commercial interactions is processed under KVKK Law No. 6698
        and GDPR guidelines as detailed in our Privacy Policy.
      </p>

      <h2>12. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, VARS is not liable for indirect, incidental, or
        consequential damages resulting from improper storage, biological misuse, or farm management
        errors beyond stated specifications.
      </p>

      <h2>13. Warranty Disclaimer</h2>
      <p>
        Products meet stated lab certificates (CoA) at dispatch. Due to biological live-feed
        variables, batch parameters are verified prior to shipment.
      </p>

      <h2>14. Compliance with Laws</h2>
      <p>
        Both parties agree to comply with international trade, customs regulations, veterinary
        health laws, and anti-corruption standards.
      </p>

      <h2>15. Force Majeure</h2>
      <p>
        Neither party is liable for failure or delay caused by events beyond reasonable control,
        including severe weather, airspace closures, pandemic quarantine, or war.
      </p>

      <h2>16. Termination</h2>
      <p>
        VARS reserves the right to suspend or terminate services or user access for material breach
        of these Terms or non-payment.
      </p>

      <h2>17. Governing Law & Jurisdiction</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of the Republic
        of Türkiye. Any disputes shall be submitted to the exclusive jurisdiction of the{" "}
        <strong>İzmir Central Courts and Execution Offices</strong>.
      </p>

      <h2>18. Changes to Terms</h2>
      <p>
        VARS reserves the right to modify these Terms at any time. Continued use of the portal
        constitutes acceptance of updated Terms.
      </p>

      <h2>19. Contact Information</h2>
      <p>
        For legal inquiries, contact us at{" "}
        <a href="mailto:info@varsco.com" className="text-primary font-bold hover:underline">
          info@varsco.com
        </a>{" "}
        or +90 232 290 57 56.
      </p>
    </LegalPage>
  );
}

/**
 * Turkish version. Authoritative for Turkish counterparties: VARS is an İzmir
 * company, clause 17 places disputes with the İzmir courts, and the referenced
 * instruments (KVKK Law No. 6698, the Distance Sales Agreement) are Turkish
 * law. Statutory identifiers match the English version and the other legal
 * routes exactly.
 */
function TermsTr() {
  return (
    <LegalPage title="Kullanım Koşulları">
      <p className="text-xs text-muted-foreground font-semibold">Yürürlük Tarihi: 01/08/2025</p>

      <h2>1. Giriş</h2>
      <p>
        İşbu Kullanım Koşulları (“Koşullar”), <strong>{COMPANY.legalName}</strong> (“VARS”, “biz”)
        tarafından sağlanan web sitesine ve ticari hizmetlere erişiminizi ve bunları kullanımınızı
        düzenler.
      </p>
      <p>
        <a
          href="https://varsco.com"
          className="text-primary font-bold hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          varsco.com
        </a>{" "}
        adresine erişerek veya satın alma siparişi göndererek aşağıdakilere uymayı kabul edersiniz:
      </p>
      <ul>
        <li>İşbu Kullanım Koşulları</li>
        <li>
          Gizlilik Politikamız:{" "}
          <Link to="/privacy" className="text-primary font-bold hover:underline">
            /privacy
          </Link>
        </li>
        <li>
          KVKK Aydınlatma Metnimiz:{" "}
          <Link to="/kvkk-disclosure-text" className="text-primary font-bold hover:underline">
            /kvkk-disclosure-text
          </Link>
        </li>
        <li>
          Mesafeli Satış Sözleşmemiz (uygulanabildiği ölçüde):{" "}
          <Link to="/distance-sales-agreement" className="text-primary font-bold hover:underline">
            /distance-sales-agreement
          </Link>
        </li>
      </ul>

      <h2>2. Şirket Bilgileri</h2>
      <p>
        <strong>Şirket Unvanı:</strong> {COMPANY.legalName}
      </p>
      <p>
        <strong>Adres:</strong> {COMPANY.address.full}
      </p>
      <p>
        <strong>Vergi Dairesi ve Vergi No:</strong> Konak V.D. 9240533729 |{" "}
        <strong>MERSİS No:</strong> 0924053372900001
      </p>
      <p>
        <strong>E-posta:</strong>{" "}
        <a href="mailto:info@varsco.com" className="text-primary font-bold">
          info@varsco.com
        </a>{" "}
        | <strong>Telefon:</strong> +90 232 290 57 56
      </p>

      <h2>3. Hizmetlerin Kapsamı</h2>
      <p>
        VARS, aşağıdakiler dâhil olmak üzere kurumsal düzeyde B2B su ürünleri yetiştiriciliği
        girdileri ve hizmetleri sunmaktadır:
      </p>
      <ul>
        <li>
          Yüksek performanslı canlı yem (Artemia kistleri, dekapsüle Artemia, Chlorella, rotifer
          zenginleştiricileri)
        </li>
        <li>
          Sertifikalı salmonid yumurtası ve döllenmiş Atlantik / Coho / Gökkuşağı alabalığı
          yumurtaları
        </li>
        <li>Yem katkı maddeleri ve hammaddeler (buğday gluteni, balık unu/yağı ikame ürünleri)</li>
        <li>Ticari su ürünleri ihracatı (Ege levreği, çipura, alabalık, ton balığı yemi)</li>
        <li>
          Kuluçkahane mühendislik tasarımı ve Kapalı Devre Su Ürünleri Yetiştiriciliği (RAS)
          danışmanlığı
        </li>
      </ul>

      <h2>4. Diğer Sözleşmelerle İlişki</h2>
      <p>
        Belirli ticari işlemler; münferit proforma faturalar, satış sözleşmeleri veya mesafeli satış
        sözleşmeleri ile düzenlenebilir. Çelişki hâlinde imzalanmış özel ticari sözleşmeler
        öncelikli olarak uygulanır.
      </p>

      <h2>5. Kullanıcı Yükümlülükleri</h2>
      <p>
        Kullanıcılar ve satın alma yetkilileri; doğru kurumsal kayıt bilgilerini sunmayı, erişim
        bilgilerini güvenli şekilde muhafaza etmeyi ve ürünleri yalnızca meşru su ürünleri
        yetiştiriciliği faaliyetlerinde kullanmayı kabul eder.
      </p>

      <h2>6. Siparişler ve Ticari Şartlar</h2>
      <p>
        Tüm teklif talepleri, proforma faturalar ve satın alma siparişleri, VARS ihracat yönetiminin
        yazılı kabulü ve doğrulaması üzerine kesinleşir.
      </p>

      <h2>7. Ödeme Koşulları</h2>
      <p>
        Çevrimiçi ödemeler, başlıca kredi kartlarını (Visa, Mastercard, Troy, AMEX) kabul eden{" "}
        <strong>iyzico PCI-DSS Seviye 1 altyapısı</strong> üzerinden güvenli şekilde işlenir. Ticari
        banka havaleleri (SWIFT / EFT / Wire) resmî proforma fatura numarasına atıf yapmalıdır.
      </p>

      <h2>8. Sevkiyat ve Riskin Devri</h2>
      <p>
        Teslimatlar uluslararası Incoterms kuralları (FOB, CIF, EXW, CIP) çerçevesinde
        gerçekleştirilir. Risk, belirtilen Incoterm hükmüne göre devrolur. Hava kargo ve soğutmalı
        konteyner taşımacılığı sırasında soğuk zincir bütünlüğü sıcaklık kayıt cihazlarıyla
        doğrulanır.
      </p>

      <h2>9. Ürün Kullanımı ve Teknik Sorumluluk</h2>
      <p>
        Canlı yem ve biyolojik girdiler, VARS biyolojik protokollerine uyulmasını gerektirir. VARS
        teknik kılavuzlar sağlar; ancak nihai işletme yönetimi sorumluluğu alıcıya aittir.
      </p>

      <h2>10. Fikrî Mülkiyet</h2>
      <p>
        Tüm markalar, logolar, REVIVE™ marka varlıkları ve tescilli teknik kılavuzlar münhasıran
        {COMPANY.legalName}’ye aittir.
      </p>

      <h2>11. Kişisel Verilerin Korunması (KVKK)</h2>
      <p>
        Ticari ilişkiler sırasında toplanan kişisel veriler, Gizlilik Politikamızda ayrıntılı olarak
        açıklandığı üzere 6698 sayılı KVKK ve GDPR ilkeleri kapsamında işlenir.
      </p>

      <h2>12. Sorumluluğun Sınırlandırılması</h2>
      <p>
        Yürürlükteki mevzuatın izin verdiği azami ölçüde VARS; belirtilen spesifikasyonların dışında
        kalan hatalı depolama, biyolojik yanlış kullanım veya işletme yönetimi hatalarından
        kaynaklanan dolaylı, arızi veya sonuç niteliğindeki zararlardan sorumlu değildir.
      </p>

      <h2>13. Garanti Beyanı</h2>
      <p>
        Ürünler, sevkiyat anında beyan edilen laboratuvar sertifikalarına (CoA) uygundur. Canlı
        yemin biyolojik değişkenliği nedeniyle parti parametreleri sevkiyat öncesinde doğrulanır.
      </p>

      <h2>14. Mevzuata Uyum</h2>
      <p>
        Taraflar; uluslararası ticaret kurallarına, gümrük mevzuatına, veteriner sağlık mevzuatına
        ve yolsuzlukla mücadele standartlarına uymayı kabul eder.
      </p>

      <h2>15. Mücbir Sebep</h2>
      <p>
        Taraflardan hiçbiri; ağır hava koşulları, hava sahası kapanmaları, salgın kaynaklı karantina
        veya savaş gibi makul kontrolü dışındaki olaylardan kaynaklanan ifa edememe veya
        gecikmelerden sorumlu tutulamaz.
      </p>

      <h2>16. Fesih</h2>
      <p>
        VARS; işbu Koşulların esaslı ihlali veya ödemenin yapılmaması hâlinde hizmetleri ya da
        kullanıcı erişimini askıya alma veya sonlandırma hakkını saklı tutar.
      </p>

      <h2>17. Uygulanacak Hukuk ve Yetkili Mahkeme</h2>
      <p>
        İşbu Koşullar Türkiye Cumhuriyeti hukukuna tabidir ve buna göre yorumlanır. Doğabilecek
        uyuşmazlıklarda <strong>İzmir Merkez Mahkemeleri ve İcra Daireleri</strong> münhasıran
        yetkilidir.
      </p>

      <h2>18. Koşullarda Değişiklik</h2>
      <p>
        VARS, işbu Koşulları dilediği zaman değiştirme hakkını saklı tutar. Portalın kullanılmaya
        devam edilmesi, güncellenmiş Koşulların kabul edildiği anlamına gelir.
      </p>

      <h2>19. İletişim Bilgileri</h2>
      <p>
        Hukuki sorularınız için{" "}
        <a href="mailto:info@varsco.com" className="text-primary font-bold hover:underline">
          info@varsco.com
        </a>{" "}
        adresinden veya +90 232 290 57 56 numarasından bize ulaşabilirsiniz.
      </p>
    </LegalPage>
  );
}
